import { basePath, proxyPath, apiVersion, getProxyHealthUrl } from './config';

// Debe superar el timeout del proxy hacia App Engine (25s en portal-proxy).
const PROXY_TIMEOUT_MS = 28000;
const PROXY_HEALTH_TIMEOUT_MS = 3000;

function isProxyTransportError(err) {
  if (!err) return false;
  if (err.name === 'AbortError') return true;
  if (err.name === 'TypeError' || err.name === 'NetworkError') return true;
  const msg = String(err.message || '');
  return /failed to fetch|networkerror|load failed/i.test(msg);
}

function isProxyInfrastructureStatus(status) {
  return status === 408 || status === 503 || status === 504;
}

function isProxyGrayStatus(status) {
  return status === 404 || status === 500 || status === 502;
}

async function parseJsonResponse(response) {
  try {
    return await response.json();
  } catch {
    return {
      code: response.status,
      message: 'Error del servidor.',
    };
  }
}

async function isProxyHealthy() {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PROXY_HEALTH_TIMEOUT_MS);
  try {
    const response = await fetch(getProxyHealthUrl(), {
      method: 'GET',
      signal: controller.signal,
    });
    return response.status === 200;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function fetchDirectFromServer(pathAndQuery, params, reason) {
  const serverUrl = `${basePath}/${apiVersion}${pathAndQuery}`;
  const fallbackHeaders = {
    ...(params.headers || {}),
    'X-Fallback-Reason': reason,
  };
  const fallbackSecret = process.env.REACT_APP_PORTAL_FALLBACK_SECRET;
  if (fallbackSecret) {
    fallbackHeaders['X-Portal-Fallback-Secret'] = fallbackSecret;
  }

  try {
    const response = await fetch(serverUrl, {
      ...params,
      headers: fallbackHeaders,
    });
    return await parseJsonResponse(response);
  } catch (fallbackErr) {
    return fallbackErr;
  }
}

async function fetchWithProxyFallback(pathAndQuery, params) {
  const proxyUrl = `${proxyPath}/${apiVersion}${pathAndQuery}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PROXY_TIMEOUT_MS);

  try {
    const response = await fetch(proxyUrl, {
      ...params,
      signal: controller.signal,
    });

    if (isProxyInfrastructureStatus(response.status)) {
      return fetchDirectFromServer(pathAndQuery, params, 'proxy-infrastructure');
    }

    if (isProxyGrayStatus(response.status)) {
      const healthy = await isProxyHealthy();
      if (!healthy) {
        return fetchDirectFromServer(pathAndQuery, params, 'proxy-unhealthy');
      }
    }

    return await parseJsonResponse(response);
  } catch (err) {
    if (isProxyTransportError(err)) {
      return fetchDirectFromServer(pathAndQuery, params, 'proxy-unreachable');
    }

    return {
      code: 500,
      message: 'Error del servidor.',
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function getTurnApi(token, user, date) {
  const url = `${basePath}/${apiVersion}/turn/${user}&${date}`;

  const params = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
  };
  return await fetch(url, params)
    .then((response) => {
      return response.json();
    })
    .then((result) => {
      return result;
    })
    .catch((err) => {
      return err.message;
    });
}

export async function addTurnApi({ token, data }) {
  const url = `${basePath}/${apiVersion}/turn`;

  const params = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify(data),
  };

  return await fetch(url, params)
    .then((response) => {
      return response.json();
    })
    .then((result) => {
      return result;
    })
    .catch((err) => {
      return err;
    });
}

export async function addTurnPortalApi({ data, tenantId }) {
  const pathAndQuery = `/turn-portal?tenant=${tenantId}`;

  const params = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  };

  return await fetchWithProxyFallback(pathAndQuery, params);
}

export async function updateTurnApi({ token, turnId, dataTurn }) {
  const url = `${basePath}/${apiVersion}/turn/${turnId}`;

  const params = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify(dataTurn),
  };

  return await fetch(url, params)
    .then((response) => {
      return response.json();
    })
    .then((result) => {
      return result;
    })
    .catch((err) => {
      return err;
    });
}

export async function updateTurnPortalApi({ turnId, dataTurn, tenantId }) {
  const pathAndQuery = `/turn-portal/${turnId}?tenant=${tenantId}`;

  const params = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(dataTurn),
  };

  return await fetchWithProxyFallback(pathAndQuery, params);
}
export async function updateStatusTurnApi({
  shiftId,
  turnId,
  status,
  token,
  important,
}) {
  const url = `${basePath}/${apiVersion}/update-turn-status/${turnId}&${shiftId}?status=${status}&important=${important}`;

  const params = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
  };

  try {
    const response = await fetch(url, params);
    const result_1 = await response.json();
    return result_1;
  } catch (err) {
    return err;
  }
}

export async function deleteTurnApi({ shiftId, turnId, token, patientId }) {
  const url = `${basePath}/${apiVersion}/delete-turn/${turnId}&${shiftId}?portal=portal&patientId=${patientId}`;

  const params = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
  };

  return await fetch(url, params)
    .then((response) => {
      return response.json();
    })
    .then((result) => {
      return result;
    })
    .catch((err) => {
      return err;
    });
}
